import { Search, Mail, Phone, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useApp, useOwnerSnapshot } from "@/context/AppContext";

export default function OwnerClients() {
  const snapshot = useOwnerSnapshot(new Date().toISOString().slice(0, 10));
  const { updateClientNotes } = useApp();
  const [search, setSearch] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  const filtered = useMemo(
    () => (snapshot?.clients ?? []).filter((client) => client.name.toLowerCase().includes(search.toLowerCase())),
    [search, snapshot?.clients],
  );
  const selectedClient = filtered.find((client) => client.id === selectedClientId) ?? snapshot?.clients.find((client) => client.id === selectedClientId) ?? filtered[0] ?? null;
  const [notesDraft, setNotesDraft] = useState(selectedClient?.notes ?? "");

  useEffect(() => {
    if (selectedClient) {
      setNotesDraft(selectedClient.notes);
      if (!selectedClientId) {
        setSelectedClientId(selectedClient.id);
      }
    }
  }, [selectedClient, selectedClientId]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground">Clients</h1>
        <p className="text-sm text-muted-foreground">{snapshot?.clients.length ?? 0} total clients</p>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search clients..." className="pl-9" value={search} onChange={(event) => setSearch(event.target.value)} />
      </div>

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6">
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-secondary/30">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">Contact</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden sm:table-cell">Last Visit</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Bookings</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((client) => (
                  <tr key={client.id} className="hover:bg-secondary/20 transition-colors cursor-pointer" onClick={() => { setSelectedClientId(client.id); setNotesDraft(client.notes); }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                          {client.name.split(" ").map((part) => part[0]).join("")}
                        </div>
                        <span className="font-medium">{client.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" />{client.email}</div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" />{client.phone || "No phone"}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-muted-foreground">{client.lastVisit ?? "No completed visit"}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs"><Calendar className="h-3 w-3" />{client.totalBookings}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border bg-card p-5 space-y-4">
          <h2 className="font-heading text-lg font-semibold">Client Notes</h2>
          {selectedClient ? (
            <>
              <div>
                <p className="font-medium">{selectedClient.name}</p>
                <p className="text-sm text-muted-foreground">{selectedClient.email}</p>
              </div>
              <textarea
                value={notesDraft}
                onChange={(event) => setNotesDraft(event.target.value)}
                className="min-h-40 w-full rounded-lg border bg-background p-3 text-sm"
                placeholder="Add allergies, preferences, retail notes, or follow-up context"
              />
              <Button onClick={() => updateClientNotes(selectedClient.id, notesDraft)}>Save Notes</Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Select a client to view details and edit notes.</p>
          )}
        </div>
      </div>
    </div>
  );
}
